import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { orderService } from '../services/orderService';
import { customerService } from '../services/customerService';
import { useLocation } from '../contexts/LocationContext';

export function useOrderForm(editingOrder, onSave) {
  const { selectedLocation, availableLocations } = useLocation();
  const currentLocationId = selectedLocation?.id || 1;

  const [savedOrderId, setSavedOrderId] = useState(null);
  const [formData, setFormData] = useState({
    klient_id: '',
    location_id: currentLocationId,
    typ_realizacji: 'do_odbioru',
    uwagi: '',
    pozycje: [],
    shipping_method: '',
    shipping_point: null
  });

  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showCustomerSelector, setShowCustomerSelector] = useState(false);

  const [productSearch, setProductSearch] = useState('');
  const [products, setProducts] = useState([]);

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const controller = new AbortController();
    let isMounted = true;

    const loadCustomersWithCleanup = async () => {
      try {
        // getCustomers przyjmuje OBIEKT parametrow i zwraca {items,total,page,per_page}.
        // Wczesniej szedl tu controller.signal (ignorowany) i setCustomers(obiekt),
        // przez co lista klientow w zamowieniu byla zawsze pusta.
        // rola='odbiorca': dostawcy z kartoteki nie moga trafic na zamowienie klienta.
        const result = await customerService.getCustomers({ rola: 'odbiorca', per_page: 200 });
        if (isMounted) {
          setCustomers(result?.items || []);
        }
      } catch (error) {
        if (error.name === 'AbortError' || error.name === 'CanceledError') return;
        console.error('Error loading customers:', error);
      }
    };

    loadCustomersWithCleanup();
    if (!editingOrder && selectedLocation) {
      setFormData(prev => ({ ...prev, location_id: selectedLocation.id }));
    }

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [selectedLocation]);

  useEffect(() => {
    const controller = new AbortController();
    let isMounted = true;

    const loadOrderDataWithCleanup = async () => {
      if (!editingOrder?.id) return;
      try {
        setLoading(true);
        const result = await orderService.getOrder(editingOrder.id, controller.signal);
        if (!isMounted) return;
        if (result.success) {
          const order = result.data;
          setFormData({
            klient_id: order.klient_id || '',
            location_id: order.location_id || 1,
            typ_realizacji: order.typ_realizacji || 'do_odbioru',
            uwagi: order.uwagi || '',
            pozycje: order.pozycje || [],
            shipping_method: order.shipping_method || '',
            shipping_point: order.shipping_point || null
          });
          if (order.klient_id) {
            const found = customers.find(c => c.id === order.klient_id);
            setSelectedCustomer(found || {
              id: order.klient_id,
              imie: order.klient_imie || '',
              nazwisko: order.klient_nazwisko || '',
              nazwa_firmy: order.klient_firma || '',
              telefon: order.klient_telefon || '',
              email: order.klient_email || ''
            });
          }
        }
      } catch (error) {
        if (error.name === 'AbortError' || error.name === 'CanceledError') return;
        console.error('Error loading order:', error);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    if (editingOrder && customers.length > 0) {
      loadOrderDataWithCleanup();
    }

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [editingOrder, customers]);

  // loadCustomers and loadOrderData are now handled inline in useEffect with AbortController

  const searchProducts = async (query) => {
    if (!query.trim() || query.length < 2) { setProducts([]); return; }
    try {
      const result = await orderService.searchProducts(query, formData.location_id, 20);
      setProducts(result.success ? (result.data || []) : []);
    } catch (error) {
      console.error('Error searching products:', error);
      setProducts([]);
    }
  };

  const handleProductSearchChange = (value) => {
    setProductSearch(value);
    searchProducts(value);
  };

  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
    setFormData(prev => ({ ...prev, klient_id: customer.id }));
    setShowCustomerSelector(false);
  };

  const handleAddCustomer = async (newCustomer) => {
    try {
      const odpowiedz = await customerService.getCustomers({ rola: 'odbiorca', per_page: 200 });
      const updatedCustomers = odpowiedz?.items || [];
      setCustomers(updatedCustomers);
      const found = updatedCustomers.find(c =>
        c.imie === newCustomer.imie && c.nazwisko === newCustomer.nazwisko &&
        (c.phone === newCustomer.telefon || c.telefon === newCustomer.telefon)
      );
      const toSet = found || newCustomer;
      setSelectedCustomer(toSet);
      setFormData(prev => ({ ...prev, klient_id: toSet.id }));
    } catch (error) {
      console.error('Error updating customer list:', error);
      setSelectedCustomer(newCustomer);
      setFormData(prev => ({ ...prev, klient_id: newCustomer.id }));
    }
    setShowCustomerSelector(false);
  };

  const handleAddProduct = (product) => {
    const existingIndex = formData.pozycje.findIndex(p => p.produkt_id === product.id);
    if (existingIndex >= 0) {
      const next = [...formData.pozycje];
      next[existingIndex].ilosc += 1;
      setFormData(prev => ({ ...prev, pozycje: next }));
    } else {
      const price = product.has_special_price === 1 && product.special_price_brutto
        ? product.special_price_brutto
        : product.price || product.cena_sprzedazy_brutto || product.default_price_brutto || 0;
      setFormData(prev => ({
        ...prev,
        pozycje: [...prev.pozycje, {
          produkt_id: product.id,
          produkt_nazwa: product.name || product.nazwa,
          ilosc: 1,
          cena_jednostkowa: parseFloat(price),
          uwagi_pozycja: '',
          has_special_price: product.has_special_price || 0,
          special_price_brutto: product.special_price_brutto || null,
          default_price_brutto: product.default_price_brutto || null
        }]
      }));
    }
    setProductSearch('');
    setProducts([]);
  };

  const handleRemoveProduct = (index) => {
    setFormData(prev => ({ ...prev, pozycje: prev.pozycje.filter((_, i) => i !== index) }));
  };

  const handleProductChange = (index, field, value) => {
    const next = [...formData.pozycje];
    next[index][field] = field === 'ilosc' || field === 'cena_jednostkowa' ? parseFloat(value) || 0 : value;
    setFormData(prev => ({ ...prev, pozycje: next }));
  };

  const calculateTotals = () => {
    let wartoscNetto = 0;
    let wartoscBrutto = 0;
    formData.pozycje.forEach(p => {
      const brutto = (parseFloat(p.ilosc) || 0) * (parseFloat(p.cena_jednostkowa) || 0);
      wartoscBrutto += brutto;
      wartoscNetto += brutto / 1.23;
    });
    return { wartoscNetto, wartoscBrutto };
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.klient_id) newErrors.klient_id = 'Wybierz klienta';
    if (formData.pozycje.length === 0) newErrors.pozycje = 'Dodaj co najmniej jeden produkt';
    formData.pozycje.forEach((p, i) => {
      if (!p.ilosc || p.ilosc <= 0) newErrors[`pozycja_${i}_ilosc`] = 'Ilosc musi byc wieksza od 0';
      if (p.cena_jednostkowa === undefined || p.cena_jednostkowa < 0) newErrors[`pozycja_${i}_cena`] = 'Cena nie moze byc ujemna';
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      setLoading(true);
      let result;
      if (editingOrder || savedOrderId) {
        result = await orderService.updateOrder(editingOrder?.id || savedOrderId, formData);
      } else {
        result = await orderService.createOrder(formData);
      }
      if (result.success) {
        if (!editingOrder && !savedOrderId && result.data?.id &&
            (formData.shipping_method === 'inpost_locker_standard' || formData.shipping_method === 'inpost_courier_standard')) {
          setSavedOrderId(result.data.id);
          toast.success('Zamowienie zapisane! Teraz mozesz wygenerowac etykiete InPost.');
        } else {
          onSave();
        }
      } else {
        setErrors({ general: result.error || 'Wystapil blad podczas zapisywania' });
      }
    } catch (error) {
      console.error('Error saving order:', error);
      setErrors({ general: 'Wystapil blad podczas zapisywania zamowienia' });
    } finally {
      setLoading(false);
    }
  };

  return {
    savedOrderId,
    formData, setFormData,
    customers,
    selectedCustomer, setSelectedCustomer,
    showCustomerSelector, setShowCustomerSelector,
    productSearch, products,
    loading, errors,
    selectedLocation, availableLocations,
    handleProductSearchChange,
    handleCustomerSelect,
    handleAddCustomer,
    handleAddProduct,
    handleRemoveProduct,
    handleProductChange,
    calculateTotals,
    handleSubmit,
  };
}
